import moment from "moment";

import { type TileDescriptor } from "@features/metrics-explorer/types";
import { type ITimeGranularityV2 } from "@north-beam/nb-common";

import { STACKED_CHART_SUPPORTED_METRICS } from "./constants";
import {
  type DimensionDefinition,
  type TabView,
  type TableSelection,
  type TableState,
} from "./types";

export function getSelectionForTab(tableState: TableState, tab: TabView) {
  if (tab === "breakdown") {
    return tableState.breakdownSelection;
  }

  if (tab === "campaign") {
    return tableState.campaignSelection;
  }

  if (tab === "adset") {
    return tableState.adsetSelection;
  }

  if (tab === "ad") {
    return tableState.adSelection;
  }

  return [];
}

export function getSelectionForSelectedTab(tableState: TableState) {
  return getSelectionForTab(tableState, tableState.selectedTab);
}

export function getSelectionStateForTab(
  tableState: TableState,
  tab: TabView,
  selectedRows: TableSelection[],
) {
  if (tab === "breakdown") {
    return { ...tableState, breakdownSelection: selectedRows };
  }

  if (tab === "campaign") {
    return { ...tableState, campaignSelection: selectedRows };
  }

  if (tab === "adset") {
    return { ...tableState, adsetSelection: selectedRows };
  }

  if (tab === "ad") {
    return { ...tableState, adSelection: selectedRows };
  }

  return tableState;
}

export function getSelectionStateForSelectedTab(
  tableState: TableState,
  selectedRows: TableSelection[],
) {
  return getSelectionStateForTab(
    tableState,
    tableState.selectedTab,
    selectedRows,
  );
}

export function buildAdvancedSearchJson(
  searchText: string,
  dimension: DimensionDefinition,
) {
  if (!searchText) {
    return null;
  }

  return {
    contains: [
      {
        var: dimension.id,
      },
      searchText,
    ],
  };
}

export function buildMetricsExplorerAdvancedSearchJson(tableState: TableState) {
  const rows = getSelectionForSelectedTab(tableState);
  const varName =
    tableState.selectedTab === "breakdown"
      ? tableState.selectedBreakdownId
      : "adKey";

  if (rows.length === 0) {
    return null;
  }

  return { or: rows.map((row) => ({ "==": [{ var: varName }, row.id] })) };
}

export function mapBenchmarkMetric(
  availableMetricIds: string[],
  metricId: string,
) {
  if (metricId === ":benchmark:") {
    availableMetricIds.find((e) => !e.endsWith("Ft") && !e.endsWith("Rtn"));
  } else if (metricId === ":benchmarkFt:") {
    return availableMetricIds.find((e) => e.endsWith("Ft"));
  } else if (metricId === ":benchmarkRtn:") {
    return availableMetricIds.find((e) => e.endsWith("Rtn"));
  } else {
    return metricId;
  }
}

export function selectedTabToLevel(selectedTab: TabView) {
  return selectedTab === "breakdown" ? "campaign" : selectedTab;
}

export function isStackedChartAvailable(metrics: TileDescriptor[]) {
  return (
    metrics[0] && STACKED_CHART_SUPPORTED_METRICS.includes(metrics[0].metricId)
  );
}

export function minDateForTimeGranularity(timeGranularity: ITimeGranularityV2) {
  if (timeGranularity === "hourly") {
    const clientTimeZone = moment.tz.guess();
    const formatDaysAgo = (days: number) =>
      moment()
        .tz(clientTimeZone)
        .startOf("day")
        .subtract(days, "days")
        .format("YYYY-MM-DD");

    return {
      minDate: formatDaysAgo(7),
      minCompareDate: formatDaysAgo(14),
    };
  }

  return { minDate: undefined, minCompareDate: undefined };
}
